import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const Contact = () => {
  const { t, lang } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(lang === "hi" ? "संदेश भेजा गया। धन्यवाद!" : "Message sent. Thank you!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="contact-page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-14">
        <div className="animate-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold mb-3">{t("contact")}</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl text-[#1B4332] mb-4 leading-[1.05]">{t("contactTitle")}</h1>
          <p className="text-lg text-[#4A4A4A] mb-10">{t("contactSub")}</p>

          <ul className="space-y-6">
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-[#C95B24]" /></div>
              <div>
                <div className="font-semibold text-[#1B4332]">{lang === "hi" ? "पता" : "Address"}</div>
                <div className="text-[#4A4A4A]">276, Gali No. 2, Vijay Nagar Chauraha,<br />Etawah, Uttar Pradesh 206001, India</div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=276+Gali+No+2+Vijay+Nagar+Chauraha+Etawah+206001"
                  target="_blank" rel="noreferrer"
                  className="text-xs text-[#C95B24] hover:underline inline-block mt-1"
                  data-testid="contact-map-link"
                >
                  {lang === "hi" ? "गूगल मैप्स पर देखें →" : "View on Google Maps →"}
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-[#C95B24]" /></div>
              <div>
                <div className="font-semibold text-[#1B4332]">Email</div>
                <a href="mailto:Info@dharmodaysevasansthan.com" className="text-[#4A4A4A] hover:text-[#C95B24] break-all">Info@dharmodaysevasansthan.com</a>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-[#C95B24]" /></div>
              <div>
                <div className="font-semibold text-[#1B4332]">{lang === "hi" ? "फ़ोन" : "Phone"}</div>
                <a href="tel:+919719008009" className="text-[#4A4A4A] hover:text-[#C95B24]">+91 97190 08009</a>
              </div>
            </li>
          </ul>

          <div className="mt-8 rounded-2xl overflow-hidden border border-[#1B4332]/10 aspect-[16/10] card-tactile" data-testid="contact-map-embed">
            <iframe
              title="Dharmoday Seva Sansthan Location"
              src="https://www.google.com/maps?q=276+Gali+No+2+Vijay+Nagar+Chauraha+Etawah+206001&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form onSubmit={submit} className="card-tactile p-8 md:p-10 space-y-5 animate-fade-up delay-1" data-testid="contact-form">
          <div>
            <Label htmlFor="c-name">{t("donateName")}</Label>
            <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-email">{t("email")}</Label>
              <Input id="c-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="c-phone">{t("donatePhone")}</Label>
              <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-sub">{t("subject")}</Label>
            <Input id="c-sub" data-testid="contact-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="c-msg">{t("message")}</Label>
            <Textarea id="c-msg" data-testid="contact-message-input" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2" />
          </div>
          <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-saffron rounded-full w-full py-6">
            <Send className="w-4 h-4 mr-2" /> {loading ? "..." : t("send")}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
